import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { companyQuery, useUpdateCompany } from '../lib/companies.js';

export function CompanyDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { data, isLoading } = useQuery(companyQuery(id));
  const updateCompany = useUpdateCompany(id);
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');

  if (isLoading) return <p className="muted">Wird geladen …</p>;
  if (!data) return null;

  const startEdit = () => {
    setName(data.name);
    setPhone(data.phone ?? '');
    setWebsiteUrl(data.websiteUrl ?? '');
    setGoogleMapsUrl(data.googleMapsUrl ?? '');
    setEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateCompany.mutateAsync({
      name,
      phone: phone || null,
      websiteUrl: websiteUrl || null,
      googleMapsUrl: googleMapsUrl || null,
    });
    setEditing(false);
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/firmen">Firmen</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{data.name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{data.name}</h1>
          {data.domain && <p className="muted text-sm">{data.domain}</p>}
        </div>
        <div>
          {!editing ? (
            <Button variant="secondary" onClick={startEdit}>Bearbeiten</Button>
          ) : (
            <Button variant="secondary" onClick={() => setEditing(false)}>Abbrechen</Button>
          )}
        </div>
      </header>

      {editing ? (
        <section className="card">
          <div className="card__body">
            <form onSubmit={handleSave} className="stack">
              <div>
                <label className="label">Name</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <label className="label">Telefon</label>
                <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div>
                <label className="label">Website URL</label>
                <input className="input" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
              </div>
              <div>
                <label className="label">Google Maps Link</label>
                <input className="input" value={googleMapsUrl} onChange={(e) => setGoogleMapsUrl(e.target.value)} />
              </div>
              <div>
                <Button type="submit" loading={updateCompany.isPending}>Speichern</Button>
              </div>
            </form>
          </div>
        </section>
      ) : (
        <div className="grid grid--2">
          <section className="card">
            <div className="card__body stack">
              <h2 style={{ fontSize: 'var(--text-lg)' }}>Google-Unternehmensprofil</h2>
              <dl className="details">
                <dt>Adresse</dt>
                <dd>{[data.street, data.postalCode, data.city, data.country].filter(Boolean).join(', ') || '—'}</dd>
                <dt>Telefon</dt>
                <dd>{data.phone ? <a href={`tel:${data.phone}`}>{data.phone}</a> : '—'}</dd>
                <dt>Website</dt>
                <dd>
                  {data.websiteUrl ? (
                    <a href={data.websiteUrl} target="_blank" rel="noopener noreferrer">
                      {data.websiteUrl}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
                <dt>Google Maps</dt>
                <dd>
                  {data.googleMapsUrl ? (
                    <a href={data.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                      In Google Maps öffnen ↗
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
                <dt>Bewertung</dt>
                <dd>
                  {data.rating ? `⭐ ${data.rating.toFixed(1)} (${data.ratingCount ?? 0} Bewertungen)` : '—'}
                </dd>
                <dt>Kategorie</dt>
                <dd>{data.category || '—'}</dd>
              </dl>
            </div>
          </section>

          <section className="card">
            <div className="card__body stack">
              <h2 style={{ fontSize: 'var(--text-lg)' }}>Verknüpfte Kontakte & Deals</h2>
              <h3>Kontakte ({data.contacts.length})</h3>
              {data.contacts.length === 0 ? (
                <p className="muted">Keine Kontakte zugeordnet.</p>
              ) : (
                <ul>
                  {data.contacts.map((c) => (
                    <li key={c.id}>
                      <Link to="/kontakte/$id" params={{ id: c.id }}>
                        {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email} ({c.email})
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <h3>Deals ({data.deals.length})</h3>
              {data.deals.length === 0 ? (
                <p className="muted">Keine Deals zugeordnet.</p>
              ) : (
                <ul>
                  {data.deals.map((d) => (
                    <li key={d.id}>
                      <strong>{d.title}</strong> — {Number(d.value).toLocaleString('de-CH')} CHF ({d.status})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
